import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Alert, Spinner, ListGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import apiClient from '../../../api/apiClient';

const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshNotifications } = useNotifications();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const statusColors = {
    open: 'primary',
    in_progress: 'info',
    resolved: 'success',
    closed: 'secondary'
  };

  useEffect(() => {
    fetchTicketDetails();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/support-tickets/${id}`);
      
      if (response.data.success) {
        setTicket(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setError('Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      setSendingMessage(true);
      const response = await apiClient.post(`/support-tickets/${id}/messages`, {
        message: newMessage.trim()
      });
      
      if (response.data.success) {
        setNewMessage('');
        await fetchTicketDetails(); // Refresh to get the new message
        setSuccess('Message sent successfully');
        setTimeout(() => setSuccess(''), 3000);
        try {
          await refreshNotifications();
        } catch (notificationError) {
          console.error('Notification refresh failed after sending ticket message:', notificationError);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.response?.data?.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('attachment', file);

      const response = await apiClient.post(`/support-tickets/${id}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        await fetchTicketDetails(); // Refresh to show the new attachment
        setSuccess('File uploaded successfully');
        setTimeout(() => setSuccess(''), 3000);
        try {
          await refreshNotifications();
        } catch (notificationError) {
          console.error('Notification refresh failed after uploading attachment:', notificationError);
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadAttachment = (attachmentId, filename) => {
    window.open(`/api/support-tickets/attachments/${attachmentId}/download`, '_blank');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="success" />
          <p className="mt-2">Loading ticket details...</p>
        </div>
      </Container>
    );
  }

  if (!ticket) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          Ticket not found or you don't have permission to view it.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate('/user/support-tickets')}
                className="me-3"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Tickets
              </Button>
              <h3 className="mb-0 d-inline">
                <code>{ticket.ticket_number}</code> - {ticket.subject}
              </h3>
            </div>
            <div>
              <Badge bg={statusColors[ticket.status]} className="me-2">
                {ticket.status.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Row>
            <Col lg={8}>
              {/* Ticket Details */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-ticket-alt me-2"></i>
                    Ticket Details
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Created:</strong> {formatDate(ticket.createdAt)}</p>
                      <p><strong>Status:</strong> 
                        <Badge bg={statusColors[ticket.status]} className="ms-2">
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </p>
                    </Col>
                    <Col md={6}>
                      {ticket.resolved_at && (
                        <p><strong>Resolved:</strong> {formatDate(ticket.resolved_at)}</p>
                      )}
                      {ticket.closed_at && (
                        <p><strong>Closed:</strong> {formatDate(ticket.closed_at)}</p>
                      )}
                    </Col>
                  </Row>
                  
                  <div className="mt-3">
                    <p><strong>Description:</strong></p>
                    <div className="bg-light p-3 rounded">
                      {ticket.description}
                    </div>
                  </div>

                  {ticket.resolution_notes && (
                    <div className="mt-3">
                      <p><strong>Resolution Notes:</strong></p>
                      <div className="bg-success bg-opacity-10 p-3 rounded border border-success">
                        {ticket.resolution_notes}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Conversation */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-comments me-2"></i>
                    Conversation
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div style={{ height: '400px', overflowY: 'auto' }} className="mb-3">
                    {ticket.messages && ticket.messages.length > 0 ? (
                      ticket.messages.map((message) => (
                        <div key={message.id} className={`mb-3 ${message.sender.id === user.id ? 'text-end' : ''}`}>
                          <div className={`d-inline-block p-3 rounded ${
                            message.sender.id === user.id 
                              ? 'bg-success text-white' 
                              : 'bg-light border'
                          }`} style={{ maxWidth: '75%' }}>
                            <div className="fw-bold mb-1">
                              {message.sender.first_name} {message.sender.last_name}
                              {message.sender.role === 0 && <Badge bg="danger" className="ms-2">Super Admin</Badge>}
                              {message.sender.role === 2 && <Badge bg="info" className="ms-2">Org Admin</Badge>}
                            </div>
                            <p className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>{message.message}</p>
                            <small className={message.sender.id === user.id ? 'text-light' : 'text-muted'}>
                              {formatDate(message.createdAt)}
                            </small>
                            
                            {/* Message attachments */}
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-2">
                                {message.attachments.map((attachment) => (
                                  <div key={attachment.id} className="d-flex align-items-center mt-1">
                                    <i className={`fas ${attachment.is_image ? 'fa-image' : 'fa-file'} me-2`}></i>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 text-decoration-none"
                                      onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename)}
                                    >
                                      {attachment.original_filename}
                                    </Button>
                                    <small className="ms-2">({formatFileSize(attachment.file_size)})</small>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="fas fa-comments fa-2x mb-2"></i>
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  {ticket.status !== 'closed' && (
                    <Form onSubmit={handleSendMessage}>
                      <Row>
                        <Col>
                          <Form.Group>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              placeholder="Type your message here..."
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              disabled={sendingMessage}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col className="d-flex justify-content-between align-items-center">
                          <div>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileUpload}
                              style={{ display: 'none' }}
                              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingFile}
                              className="me-2"
                            >
                              {uploadingFile ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-1" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-paperclip me-1"></i>
                                  Attach File
                                </>
                              )}
                            </Button>
                            <small className="text-muted">
                              Max 10MB. Supported: Images, PDF, Word, Excel, Text
                            </small>
                          </div>
                          <Button
                            variant="success"
                            type="submit"
                            disabled={sendingMessage || !newMessage.trim()}
                          >
                            {sendingMessage ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-paper-plane me-2"></i>
                                Send Message
                              </>
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  )}

                  {ticket.status === 'closed' && (
                    <Alert variant="info" className="mt-3">
                      <i className="fas fa-info-circle me-2"></i>
                      This ticket has been closed. You cannot send new messages.
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              {/* Ticket Attachments */}
              {ticket.attachments && ticket.attachments.length > 0 && (
                <Card className="mb-4">
                  <Card.Header>
                    <h6 className="mb-0">
                      <i className="fas fa-paperclip me-2"></i>
                      Attachments
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <ListGroup variant="flush">
                      {ticket.attachments.map((attachment) => (
                        <ListGroup.Item key={attachment.id} className="d-flex align-items-center px-0">
                          <i className={`fas ${attachment.is_image ? 'fa-image' : 'fa-file'} me-2 text-muted`}></i>
                          <div className="flex-grow-1">
                            <div className="fw-bold">{attachment.original_filename}</div>
                            <small className="text-muted">
                              {formatFileSize(attachment.file_size)} • 
                              Uploaded by {attachment.uploader.first_name} {attachment.uploader.last_name}
                            </small>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename)}
                          >
                            <i className="fas fa-download"></i>
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Card.Body>
                </Card>
              )}

              {/* Ticket Info */}
              <Card>
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Ticket Information
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <strong>Ticket Number:</strong>
                    <br />
                    <code>{ticket.ticket_number}</code>
                  </div>
                  
                  <div className="mb-2">
                    <strong>Created By:</strong>
                    <br />
                    {ticket.creator.first_name} {ticket.creator.last_name}
                    <br />
                    <small className="text-muted">{ticket.creator.email}</small>
                  </div>

                  {ticket.organization && (
                    <div className="mb-2">
                      <strong>Organization:</strong>
                      <br />
                      {ticket.organization.name}
                    </div>
                  )}

                  <div className="mb-2">
                    <strong>Last Updated:</strong>
                    <br />
                    {formatDate(ticket.updatedAt)}
                  </div>

                  {ticket.messages && ticket.messages.length > 0 && (
                    <div className="mb-2">
                      <strong>Messages:</strong>
                      <br />
                      {ticket.messages.length} message{ticket.messages.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default TicketDetailPage;
